import { VariableDefinition } from '../types';

/**
 * Finds the index of the matching closing delimiter for the one at startIndex.
 * Handles nesting, strings, heredocs, and comments robustly.
 * Works for both {} and [] pairs.
 */
function findMatchingDelimiter(str: string, startIndex: number): number {
  const openChar = str[startIndex];
  const closeChar = openChar === '{' ? '}' : openChar === '[' ? ']' : '}';

  let depth = 0;
  let inString = false;
  let inLineComment = false;
  let inBlockComment = false;
  let inHeredoc = false;
  let heredocTag = '';

  for (let i = startIndex; i < str.length; i++) {
    const char = str[i];
    const nextChar = str[i + 1];

    // Handle HEREDOC (takes highest priority)
    if (inHeredoc) {
      // Check if this line matches the closing heredoc tag
      if (char === '\n' || i === startIndex) {
        const lineStart = char === '\n' ? i + 1 : i;
        const restOfLine = str.substring(lineStart).match(/^\s*([A-Z_a-z0-9]+)/);
        if (restOfLine && restOfLine[1] === heredocTag) {
          inHeredoc = false;
          i = lineStart + restOfLine[0].length - 1;
        }
      }
      continue;
    }

    // Handle Strings
    if (inString) {
      if (char === '\\') {
        i++; // skip escaped char
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    // Handle Line Comments
    if (inLineComment) {
      if (char === '\n') inLineComment = false;
      continue;
    }

    // Handle Block Comments
    if (inBlockComment) {
      if (char === '*' && nextChar === '/') {
        inBlockComment = false;
        i++;
      }
      continue;
    }

    // Start of HEREDOC
    if (char === '<' && nextChar === '<') {
      const heredocMatch = str.substring(i).match(/^<<-?([A-Z_a-z][A-Z_a-z0-9]*)\s*\n/);
      if (heredocMatch) {
        inHeredoc = true;
        heredocTag = heredocMatch[1];
        i += heredocMatch[0].length - 1;
        continue;
      }
    }

    // Start of String
    if (char === '"') {
      inString = true;
      continue;
    }

    // Start of Comments
    if (char === '#' || (char === '/' && nextChar === '/')) {
      inLineComment = true;
      if (char === '/') i++;
      continue;
    }
    if (char === '/' && nextChar === '*') {
      inBlockComment = true;
      i++;
      continue;
    }

    // Delimiters - track both {} and [] independently via the target pair
    if (char === openChar) {
      depth++;
    } else if (char === closeChar) {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/**
 * Extracts the 'type' value from a variable block content.
 * Handles multi-line complex types like object({...}), map(object({...})), optional(...), etc.
 */
function extractType(content: string): string {
  // Find the 'type = ' assignment
  const typeStart = content.match(/^\s*type\s*=\s*/m);
  if (!typeStart) return 'any';

  const startIdx = typeStart.index! + typeStart[0].length;
  const remaining = content.substring(startIdx);

  // If it starts with a function call like object(...), map(...), list(...), set(...), optional(...), tuple(...)
  const funcMatch = remaining.match(
    /^(object|map|list|set|optional|tuple|number|string|bool|any)\s*\(/
  );
  if (funcMatch) {
    // Find matching closing parenthesis, respecting nested parens and braces
    let depth = 0;
    let inStr = false;
    for (let i = funcMatch[0].length - 1; i < remaining.length; i++) {
      const c = remaining[i];
      if (inStr) {
        if (c === '\\') {
          i++;
          continue;
        }
        if (c === '"') inStr = false;
        continue;
      }
      if (c === '"') {
        inStr = true;
        continue;
      }
      if (c === '(' || c === '{') depth++;
      else if (c === ')' || c === '}') {
        depth--;
        if (depth === 0) {
          return remaining.substring(0, i + 1).trim();
        }
      }
    }
  }

  // Simple type keyword on the same line
  const simpleMatch = remaining.match(/^([^\n#/]+)/);
  if (simpleMatch) {
    return simpleMatch[1].trim();
  }

  return 'any';
}

/**
 * Extracts the 'description' value from a variable block.
 */
function extractDescription(content: string): string {
  const match = content.match(/description\s*=\s*"((?:[^"\\]|\\.)*)"/);
  if (match) {
    return match[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
  }
  // Try HEREDOC style description
  const heredoc = content.match(
    /description\s*=\s*<<-?([A-Z_a-z][A-Z_a-z0-9]*)\s*\n([\s\S]*?)\n\s*\1/
  );
  if (heredoc) {
    return heredoc[2].trim();
  }
  return '';
}

/**
 * Extracts the 'default' value as a raw string.
 * Handles primitives, lists, maps, objects, heredocs, null, and function calls.
 */
function extractDefault(content: string): string | null {
  const defaultRegex = /^\s*default\s*=\s*/m;
  const match = defaultRegex.exec(content);

  if (!match) return null;

  const startIdx = match.index + match[0].length;
  const remaining = content.substring(startIdx).trimStart();

  if (remaining.length === 0) return null;

  const firstChar = remaining[0];

  // 1. null literal
  if (remaining.startsWith('null')) {
    return 'null';
  }

  // 2. Complex Object or List
  if (firstChar === '{' || firstChar === '[') {
    // Use the robust delimiter matcher on the remaining substring
    const endIdx = findMatchingDelimiter(remaining, 0);
    if (endIdx !== -1) {
      return remaining.substring(0, endIdx + 1);
    }
  }

  // 3. HEREDOC
  if (remaining.startsWith('<<')) {
    const heredocMatch = remaining.match(/^<<-?([A-Z_a-z][A-Z_a-z0-9]*)\s*\n([\s\S]*?)\n\s*\1/);
    if (heredocMatch) return heredocMatch[0];
  }

  // 4. Quoted string (handle multiline/escaped)
  if (firstChar === '"') {
    for (let i = 1; i < remaining.length; i++) {
      if (remaining[i] === '\\') {
        i++;
        continue;
      }
      if (remaining[i] === '"') {
        return remaining.substring(0, i + 1);
      }
    }
  }

  // 5. Simple Primitive: boolean, number, or reference/function call until newline or comment
  const primitiveMatch = remaining.match(/^(true|false|[\d.]+(?:e[+-]?\d+)?)/i);
  if (primitiveMatch) {
    return primitiveMatch[0];
  }

  // 6. Function call or reference (e.g., toset([...]), merge(...), var.xxx)
  const funcCallMatch = remaining.match(/^([a-z_][a-z_0-9.]*)\s*\(/i);
  if (funcCallMatch) {
    // Find matching closing paren
    const parenStart = remaining.indexOf('(');
    let depth = 0;
    let inStr = false;
    for (let i = parenStart; i < remaining.length; i++) {
      const c = remaining[i];
      if (inStr) {
        if (c === '\\') {
          i++;
          continue;
        }
        if (c === '"') inStr = false;
        continue;
      }
      if (c === '"') {
        inStr = true;
        continue;
      }
      if (c === '(' || c === '[' || c === '{') depth++;
      else if (c === ')' || c === ']' || c === '}') {
        depth--;
        if (depth === 0) {
          return remaining.substring(0, i + 1);
        }
      }
    }
  }

  return null;
}

/**
 * Parses raw variables.tf content locally using robust Regex and brace counting.
 * This is the primary parser — no AI needed for standard and complex HCL.
 */
export const parseVariablesLocally = (hcl: string): VariableDefinition[] => {
  const variables: VariableDefinition[] = [];

  // Regex to find start of variable blocks
  const varStartRegex = /variable\s+"([^"]+)"\s*\{/g;
  let match;

  while ((match = varStartRegex.exec(hcl)) !== null) {
    const name = match[1];
    const openBraceIndex = match.index + match[0].length - 1;

    // Find the closing brace of this variable block
    const endIndex = findMatchingDelimiter(hcl, openBraceIndex);

    if (endIndex !== -1) {
      const blockContent = hcl.substring(openBraceIndex + 1, endIndex);

      const type = extractType(blockContent);
      const description = extractDescription(blockContent);
      const defaultValue = extractDefault(blockContent);

      variables.push({
        name,
        type,
        description,
        default: defaultValue === null ? undefined : defaultValue,
        required: defaultValue === null,
      });

      // Advance past this block
      varStartRegex.lastIndex = endIndex + 1;
    }
  }

  return variables;
};
